/**
 * Shared visual primitives for the guest portal's Accommodation tab — a single
 * source of truth for the modern, mobile-first card system so every section
 * looks consistent (clean borders, soft elevation, refined spacing) instead of
 * each block re-declaring its own gradient/shadow inline.
 *
 * Design language (2026 refresh):
 *  - Soft, low-contrast surfaces with a single hairline border.
 *  - Generous radius (2xl) and consistent padding rhythm.
 *  - A small gradient "icon badge" anchors each section header.
 *  - Subtle, not noisy: shadows are light; colour is an accent, not the surface.
 */
import { useState, type ReactNode, type ElementType } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** A consistent surface card. Use `as` to render a different element. */
export function GuestCard({
  children,
  darkMode,
  className,
  padded = true,
  interactive = false,
}: {
  children: ReactNode;
  darkMode: boolean;
  className?: string;
  /** Apply the standard inner padding (set false when the card owns its own). */
  padded?: boolean;
  /** Slight hover lift for tappable cards. */
  interactive?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-3xl overflow-hidden border transition-all duration-200',
        // Cards sit ABOVE the (deeper, blue-tinted) page background: solid
        // surface + a clear border/shadow so they visibly lift off the page.
        darkMode
          ? 'bg-slate-800 border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-white border-gray-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_12px_32px_-14px_rgba(15,23,42,0.22)]',
        interactive && (darkMode ? 'hover:bg-slate-700/80 hover:border-slate-600' : 'hover:shadow-[0_2px_6px_rgba(0,0,0,0.07),0_16px_40px_-14px_rgba(15,23,42,0.3)] hover:border-gray-300/80'),
        padded && 'p-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

const ACCENTS: Record<string, { from: string; to: string; glow: string }> = {
  cyan: { from: 'from-cyan-400', to: 'to-blue-500', glow: 'shadow-cyan-500/30' },
  emerald: { from: 'from-emerald-400', to: 'to-teal-500', glow: 'shadow-emerald-500/30' },
  amber: { from: 'from-amber-400', to: 'to-orange-500', glow: 'shadow-amber-500/30' },
  violet: { from: 'from-violet-400', to: 'to-purple-500', glow: 'shadow-violet-500/30' },
  rose: { from: 'from-rose-400', to: 'to-pink-500', glow: 'shadow-rose-500/30' },
};

/**
 * A section divider with a gradient icon badge, a bold title and a hairline rule.
 * Anchors each group on the Accommodation tab for a clear visual hierarchy.
 */
export function SectionHeader({
  icon: Icon,
  title,
  accent = 'cyan',
  darkMode,
  count,
}: {
  icon: ElementType;
  title: string;
  accent?: keyof typeof ACCENTS;
  darkMode: boolean;
  /** Optional small count pill (e.g. number of items in the section). */
  count?: number;
}) {
  const a = ACCENTS[accent] ?? ACCENTS.cyan;
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className={cn('relative grid place-items-center h-11 w-11 rounded-2xl bg-gradient-to-br shadow-lg', a.from, a.to, a.glow)}>
        <Icon className="h-6 w-6 text-white drop-shadow-sm" />
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/25 via-transparent to-transparent" />
      </div>
      <h2 className={cn('font-bold text-lg tracking-tight', darkMode ? 'text-white' : 'text-gray-900')}>
        {title}
      </h2>
      {typeof count === 'number' && count > 0 && (
        <span className={cn('grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-[11px] font-bold', darkMode ? 'bg-slate-700 text-slate-200' : 'bg-gray-100 text-gray-600')}>
          {count}
        </span>
      )}
      <div className={cn('flex-1 h-px', darkMode ? 'bg-slate-700/60' : 'bg-gray-200/80')} />
    </div>
  );
}

const ICON_TINT: Record<string, { light: string; dark: string; bgLight: string; bgDark: string }> = {
  cyan: { light: 'text-cyan-600', dark: 'text-cyan-400', bgLight: 'bg-cyan-50', bgDark: 'bg-cyan-500/15' },
  emerald: { light: 'text-emerald-600', dark: 'text-emerald-400', bgLight: 'bg-emerald-50', bgDark: 'bg-emerald-500/15' },
  amber: { light: 'text-amber-600', dark: 'text-amber-400', bgLight: 'bg-amber-50', bgDark: 'bg-amber-500/15' },
  violet: { light: 'text-violet-600', dark: 'text-violet-400', bgLight: 'bg-violet-50', bgDark: 'bg-violet-500/15' },
  rose: { light: 'text-rose-600', dark: 'text-rose-400', bgLight: 'bg-rose-50', bgDark: 'bg-rose-500/15' },
  blue: { light: 'text-blue-600', dark: 'text-blue-400', bgLight: 'bg-blue-50', bgDark: 'bg-blue-500/15' },
  orange: { light: 'text-orange-600', dark: 'text-orange-400', bgLight: 'bg-orange-50', bgDark: 'bg-orange-500/15' },
};

/**
 * A small "scope" badge telling the guest whether something belongs to THIS unit
 * only or is SHARED with the whole property. Central so every facility/amenity
 * uses the same visual language (blue = private/unit, cyan = shared).
 */
export function ScopeBadge({
  scope,
  label,
  darkMode,
}: {
  scope: 'unit' | 'shared';
  label: string;
  darkMode: boolean;
}) {
  return (
    <span
      className={cn(
        'shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide',
        scope === 'unit'
          ? darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
          : darkMode ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700',
      )}
    >
      {label}
    </span>
  );
}

/**
 * A topic row that expands IN PLACE (accordion). The backbone of the topic-first
 * Accommodation tab: a clean collapsed header (icon + title + optional summary +
 * optional badge) that reveals all of a topic's detail on tap. Multiple rows can
 * be open at once (each owns its own state) so guests can compare topics.
 *
 * Empty-section rule: the CALLER must only render a TopicAccordion when it has
 * content to show inside — never gate the header differently from the body.
 */
export function TopicAccordion({
  icon: Icon,
  title,
  summary,
  accent = 'cyan',
  badge,
  defaultOpen = false,
  darkMode,
  children,
}: {
  icon: ElementType;
  title: string;
  /** Optional one-line preview shown under the title while collapsed. */
  summary?: string;
  accent?: keyof typeof ICON_TINT;
  /** Optional trailing badge (e.g. a ScopeBadge). */
  badge?: ReactNode;
  defaultOpen?: boolean;
  darkMode: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const tint = ICON_TINT[accent] ?? ICON_TINT.cyan;

  return (
    <GuestCard darkMode={darkMode} padded={false}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'w-full flex items-center gap-3 p-4 text-left transition-colors',
          darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-gray-50/80',
        )}
      >
        <div className={cn('grid place-items-center h-11 w-11 rounded-2xl shrink-0', darkMode ? tint.bgDark : tint.bgLight)}>
          <Icon className={cn('h-5 w-5', darkMode ? tint.dark : tint.light)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={cn('font-bold text-base tracking-tight truncate', darkMode ? 'text-white' : 'text-gray-900')}>
              {title}
            </h3>
            {badge}
          </div>
          {summary && (
            <p className={cn('text-xs mt-0.5 truncate', darkMode ? 'text-slate-400' : 'text-gray-500')}>
              {summary}
            </p>
          )}
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 shrink-0 transition-transform duration-200',
            darkMode ? 'text-slate-400' : 'text-gray-400',
            open && 'rotate-180',
          )}
        />
      </button>

      <div
        className={cn(
          'grid transition-all duration-300 ease-in-out',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className={cn('px-4 pb-4 pt-1', darkMode ? 'border-t border-slate-700/50' : 'border-t border-gray-100')}>
            {children}
          </div>
        </div>
      </div>
    </GuestCard>
  );
}

export default GuestCard;
